import { z } from 'zod';
import { MessengerOutcomeEventTypeSchema, MessengerSchema } from './messenger-outcome';

/** Which audience the line a message came in on serves (IMM-1213). */
export const MessengerLineSchema = z.enum(['fleet', 'members']);
export type MessengerLine = z.infer<typeof MessengerLineSchema>;

/** The prefix of a Telegram `providerMessageId` per bot: Telegram numbers messages per bot, so two bots never share one. */
export const TELEGRAM_PROVIDER_MESSAGE_ID_PREFIX = {
  fleet: 'telegram',
  members: 'telegram-product',
} as const satisfies Record<MessengerLine, string>;

/**
 * One inbound messenger message, normalised by the webhook parser whatever the provider (ADR 0063): the
 * communications router never sees a provider payload.
 */
export const MessengerMessageReceivedPayloadSchema = z.object({
  messenger: MessengerSchema,
  /**
   * Unique across messengers: a wamid on WhatsApp, `telegram:<chatId>:<messageId>` on the tech bot,
   * `telegram-product:<chatId>:<messageId>` on the product bot.
   */
  providerMessageId: z.string().min(1),
  /** The sender's account on that messenger: the E.164 number on WhatsApp, the chat id on Telegram. */
  account: z.string().min(1),
  /** What the profile shows for the account (`@username` on Telegram); null when the account itself is the label. */
  accountLabel: z.string().nullable(),
  senderName: z.string().nullable(),
  /** Only a private chat is a person's own account; a group never connects or reaches a member's chat. */
  privateChat: z.boolean(),
  /** The text; null for anything immoOS cannot render. */
  body: z.string().nullable(),
  /** What the message was when it is not text (`image`, `media`, …); null for a text message. */
  unsupportedType: z.string().nullable(),
  /**
   * The message this one quotes, in `providerMessageId`'s format: Telegram's reply-to, WhatsApp's context. Absent on
   * events written before OPS-79; null when it quotes nothing.
   */
  replyToProviderMessageId: z.string().min(1).nullable().optional(),
  /**
   * Set when the message is a tap on a button the bot sent (Telegram `callback_query`): `body` is then the button's
   * key, and the provider wants the tap answered and the buttons taken off the message it sat on (IMM-1097).
   */
  choice: z
    .object({
      callbackQueryId: z.string().min(1),
      /** The message the button was on, in `providerMessageId`'s format. */
      messageProviderMessageId: z.string().min(1),
      /** That message's text, so the chosen option can be appended to it; null when the provider did not send it. */
      messageText: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * Set when the bot the message came in on serves one audience only (IMM-1213, Telegram): `fleet` — the tech bot,
   * fleet questions and their answers; `members` — the product bot, the member chat. Absent: the line serves both —
   * WhatsApp's one number, or a Telegram update written before the split.
   */
  line: MessengerLineSchema.optional(),
  receivedAt: z.iso.datetime(),
});
export type MessengerMessageReceivedPayload = z.infer<typeof MessengerMessageReceivedPayloadSchema>;

export const MESSENGER_DISCUSSION_CONTEXT_SOURCES = ['quote', 'session'] as const;

/** A fleet person's text on the fleet line (IMM-1420): what the session layer used to take as an answer. */
export const MessengerDiscussionReceivedPayloadSchema = z.object({
  messenger: MessengerSchema,
  providerMessageId: z.string().min(1),
  account: z.string().min(1),
  senderName: z.string().nullable(),
  body: z.string(),
  receivedAt: z.iso.datetime(),
  /** The session the message quotes or was written into; null when neither. Opaque to communications. */
  context: z
    .object({
      source: z.enum(MESSENGER_DISCUSSION_CONTEXT_SOURCES),
      outcomeEvent: MessengerOutcomeEventTypeSchema,
      ref: z.record(z.string(), z.string()),
    })
    .nullable(),
});
export type MessengerDiscussionReceivedPayload = z.infer<typeof MessengerDiscussionReceivedPayloadSchema>;
